import React from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogTrigger,
  Select,
  Text,
  TextArea,
  type SelectOption,
} from '@chatsdet/ui';
import { testSignInRedirectUriError } from '@chatsdet/types';
import {
  useCreateClientMutation,
  useUpdateClientMutation,
} from './useTestSignIn';
import type { TestSignInClient } from './types';

type PropertyOption = { id: string; label: string };

type Props = {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  /** Properties without an existing client (create mode only). */
  availableProperties: PropertyOption[];
  /** Present when editing an existing client's callback URLs. */
  editingClient?: { client: TestSignInClient; propertyLabel: string } | null;
  onCreated: (result: { client: TestSignInClient; clientSecret: string }) => void;
};

function parseUris(raw: string): string[] {
  return raw
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean);
}

export function TestSignInAddClientDialog({
  isOpen,
  onOpenChange,
  availableProperties,
  editingClient,
  onCreated,
}: Props) {
  const isEditing = !!editingClient;
  const [propertyId, setPropertyId] = React.useState<string | null>(null);
  const [urisText, setUrisText] = React.useState('');

  const createMutation = useCreateClientMutation();
  const updateMutation = useUpdateClientMutation();
  const isPending = createMutation.isPending || updateMutation.isPending;

  React.useEffect(() => {
    if (!isOpen) return;
    if (editingClient) {
      setPropertyId(null);
      setUrisText(editingClient.client.redirectUris.join('\n'));
    } else {
      setPropertyId(availableProperties[0]?.id ?? null);
      setUrisText('');
    }
    createMutation.reset();
    updateMutation.reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, editingClient?.client.id]);

  const uris = parseUris(urisText);
  const uriErrors = uris.map((uri) => testSignInRedirectUriError(uri));
  const hasUriErrors = uriErrors.some((e) => e !== null);
  const isValid =
    uris.length > 0 && !hasUriErrors && (isEditing || !!propertyId);

  const error = createMutation.error ?? updateMutation.error;

  const propertyItems: SelectOption[] = availableProperties.map((p) => ({
    id: p.id,
    label: p.label,
  }));

  const handleSubmit = async () => {
    if (isEditing) {
      await updateMutation.mutateAsync({ clientId: editingClient!.client.id, redirectUris: uris });
      onOpenChange(false);
    } else if (propertyId) {
      const result = await createMutation.mutateAsync({ propertyId, redirectUris: uris });
      onOpenChange(false);
      onCreated(result.data);
    }
  };

  return (
    <DialogTrigger size="lg" isOpen={isOpen} onOpenChange={onOpenChange}>
      <span />
      <Dialog title={isEditing ? 'Edit callback URLs' : 'Add application'}>
        <DialogContent>
          <Box className="space-y-4">
            {isEditing ? (
              <Box>
                <Text size="sm" weight="medium">
                  Application
                </Text>
                <Text size="sm" className="text-muted-foreground">
                  {editingClient!.propertyLabel}
                </Text>
              </Box>
            ) : (
              <Select
                label="Application"
                items={propertyItems}
                selectedKey={propertyId ?? undefined}
                onSelectionChange={(key) => setPropertyId(String(key))}
                isDisabled={isPending || propertyItems.length === 0}
                placeholder={propertyItems.length === 0 ? 'No applications available' : undefined}
                description={
                  propertyItems.length === 0
                    ? 'Every application already has a test sign-in client.'
                    : undefined
                }
              />
            )}
            <Box>
              <TextArea
                label="Callback URLs"
                value={urisText}
                onChange={setUrisText}
                isDisabled={isPending}
                rows={4}
                placeholder={'https://staging.example.com/api/auth/callback/cofactor'}
                description="One exact https URL per line. No wildcards — OIDC matches redirect_uri by exact string."
              />
              {uris.length > 0 && hasUriErrors && (
                <Box className="mt-2 space-y-1">
                  {uris.map((uri, i) =>
                    uriErrors[i] ? (
                      <Text key={i} size="xs" className="text-destructive">
                        {uri}: {uriErrors[i]}
                      </Text>
                    ) : null
                  )}
                </Box>
              )}
            </Box>
            {error && (
              <Text size="sm" className="text-destructive">
                {error instanceof Error ? error.message : 'Failed to save'}
              </Text>
            )}
          </Box>
        </DialogContent>
        <DialogFooter>
          <Button variant="ghost" onPress={() => onOpenChange(false)} isDisabled={isPending}>
            Cancel
          </Button>
          <Button variant="primary" onPress={handleSubmit} isDisabled={!isValid || isPending}>
            {isPending ? 'Saving...' : isEditing ? 'Save' : 'Add application'}
          </Button>
        </DialogFooter>
      </Dialog>
    </DialogTrigger>
  );
}
